import { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { useModal } from 'mui-modal-provider';
import { useNavigate } from 'react-router-dom';

import { CvModalBase } from '../../../cv-modals/cv-modal-base';
import { INotificationNc } from '../../../../../../graphql/app-level-modals/models/get-modals-data.model';
import { NotificationType } from '../../../../../../models/notification.models';
import { getControlVisitPointsNotifications } from '../../utils';
import { InternalLinks } from '../../../../../../constants';
import { UPDATE_NC_NOTIFICATION } from '../../../../../../graphql/notifications/mutations/update-nc-notification';
import { useActionsInProgress } from '../../../../../../graphql';
import { promiseErrorCallbacks } from '../../../../../../utils/promise/set-promise-error-callbacks';
import { useCancellablePromise } from '../../../../../../hooks';

const getPointsFromData = (data: string): number | null => {
  try {
    const points = Number(JSON.parse(data)?.points);

    return Number.isFinite(points) ? points : null;
  } catch {
    return null;
  }
};

export const useControlVisitPointsModal = () => {
  const [isShow, setIsShow] = useState(true);
  const { showModal } = useModal();
  const navigate = useNavigate();
  const [updateNotification] = useMutation(UPDATE_NC_NOTIFICATION);
  const { addActionInProgress, removeActionInProgress } = useActionsInProgress();
  const {
    makeCancellablePromise,
    CancelledPromiseOnUnmountError,
  } = useCancellablePromise();

  const handleViewLenses = useCallback((isUpgrade: boolean): void => {
    if (isUpgrade) {
      navigate(InternalLinks.myLensesPage);
    }
  }, [navigate]);

  const handleControlVisitPointsModal = useCallback((notifications: INotificationNc[]): void => {
    if (!isShow) return;

    const cvPointsNotification = getControlVisitPointsNotifications(notifications)[0];

    if (!cvPointsNotification || cvPointsNotification.acknowledged) return;

    const points = getPointsFromData(cvPointsNotification.data);

    if (points == null) return;

    const isUpgrade = cvPointsNotification.type === NotificationType.CONTROL_VISIT_POINTS_WITH_UPGRADE;

    const acknowledge = async (): Promise<void> => {
      try {
        addActionInProgress();

        await makeCancellablePromise(
          updateNotification({
            variables: {
              notificationId: cvPointsNotification.id,
            },
          }),
        );
      } catch (error) {
        if (error instanceof CancelledPromiseOnUnmountError) {
          return;
        }

        if (promiseErrorCallbacks.anyError) {
          promiseErrorCallbacks.anyError();
        }
      } finally {
        removeActionInProgress();
      }
    };

    const modal = showModal(CvModalBase, {
      imageText: String(points),
      title: <FormattedMessage id="controlVisitPoints.title" values={{ points }} />,
      text: isUpgrade ? 'controlVisitPoints.description.upgrade' : 'controlVisitPoints.description',
      button: isUpgrade ? 'controlVisitPoints.button.viewLenses' : 'common.gotIt',
      buttonTestId: 'cv-points-button',
      onAction: () => handleViewLenses(isUpgrade),
      veevaNumber: '2026PP10366',
      isVeevaSmall: true,
      onClose: async (): Promise<void> => {
        await acknowledge();
        modal.hide();
      },
    });

    setIsShow(false);
  }, [
    isShow,
    showModal,
    handleViewLenses,
    updateNotification,
    addActionInProgress,
    removeActionInProgress,
    makeCancellablePromise,
    CancelledPromiseOnUnmountError,
  ]);

  return { handleControlVisitPointsModal };
};
